export interface NormalizeStakeOptions {
  betMin: number
  betMax: number
  payoutMax: number
  odds: number
  drawCount?: number
  numBets?: number
  allowDecimals?: boolean
  /** Remaining payout cap after other betslip lines (betslip-level payout_max mode). */
  remainingPayoutMax?: number
}

export interface BetLinePotentialInput {
  stake: number
  odds: number
  drawCount?: number
  numBets?: number
}

/** API/game-settings may return numeric fields as strings. */
export function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

function floorStakeAmount(stake: number, allowDecimals: boolean): number {
  if (!allowDecimals) {
    return Math.floor(stake)
  }
  return Math.floor(stake * 100) / 100
}

export function calcPotentialWin(
  stake: number,
  odds: number,
  drawCount = 1,
  numBets = 1
): number {
  const stakeValue = toNumber(stake)
  const oddsValue = toNumber(odds)
  if (!stakeValue || !oddsValue) return 0
  return stakeValue * oddsValue * drawCount * numBets
}

export function sumPotentialWins(lines: BetLinePotentialInput[]): number {
  return lines.reduce(
    (total, line) =>
      total +
      calcPotentialWin(
        line.stake,
        line.odds,
        line.drawCount ?? 1,
        line.numBets ?? 1
      ),
    0
  )
}

export function capTotalPotentialWin(totalPotential: number, payoutMax: number): number {
  const payoutCap = toNumber(payoutMax)
  const potential = toNumber(totalPotential)
  if (payoutCap <= 0) return potential
  return potential > payoutCap ? payoutCap : potential
}

export function capStakeForPayoutMax(
  stake: number,
  odds: number,
  payoutMax: number,
  drawCount = 1,
  numBets = 1,
  allowDecimals = true
): number {
  const stakeValue = toNumber(stake)
  const oddsValue = toNumber(odds)
  const payoutCap = toNumber(payoutMax)
  if (payoutCap <= 0 || oddsValue <= 0) return stakeValue
  const multiplier = oddsValue * drawCount * numBets
  if (multiplier <= 0) return stakeValue
  return floorStakeAmount(payoutCap / multiplier, allowDecimals)
}

export function normalizeStakeValue(
  rawStake: number,
  options: NormalizeStakeOptions
): number {
  const allowDecimals = options.allowDecimals ?? true
  let stakeValue = toNumber(rawStake)
  if (stakeValue <= 0) {
    return 0
  }
  const betMin = toNumber(options.betMin)
  const betMax = toNumber(options.betMax)
  const payoutMax = toNumber(
    options.remainingPayoutMax !== undefined
      ? options.remainingPayoutMax
      : options.payoutMax
  )
  const odds = toNumber(options.odds)
  const drawCount = options.drawCount ?? 1
  const numBets = options.numBets ?? 1

  if (betMin > 0 && stakeValue < betMin) {
    stakeValue = betMin
  }

  if (betMax > 0 && stakeValue > betMax) {
    stakeValue = betMax
  }

  if (payoutMax > 0 && odds > 0) {
    const maxStakeForPayout = capStakeForPayoutMax(
      stakeValue,
      odds,
      payoutMax,
      drawCount,
      numBets,
      allowDecimals
    )
    if (stakeValue > maxStakeForPayout) {
      stakeValue = maxStakeForPayout
    }
  }

  stakeValue = floorStakeAmount(stakeValue, allowDecimals)

  if (betMin > 0 && stakeValue > 0 && stakeValue < betMin) {
    const potentialAtMin = calcPotentialWin(betMin, odds, drawCount, numBets)
    if (payoutMax > 0 && potentialAtMin > payoutMax) {
      return stakeValue
    }
    stakeValue = betMin
  }

  return stakeValue
}

export function normalizeBetslipStakesSequentially(
  lines: BetLinePotentialInput[],
  options: Pick<
    NormalizeStakeOptions,
    'betMin' | 'betMax' | 'payoutMax' | 'allowDecimals'
  >
): number[] {
  const cap = toNumber(options.payoutMax)
  const allowDecimals = options.allowDecimals ?? true

  if (cap <= 0 || lines.length === 0) {
    return lines.map((line) =>
      normalizeStakeValue(line.stake, {
        ...options,
        payoutMax: 0,
        odds: line.odds,
        drawCount: line.drawCount ?? 1,
        numBets: line.numBets ?? 1,
      })
    )
  }

  const normalizeLineStake = (
    stake: number,
    line: BetLinePotentialInput,
    payoutLimit: number,
    remainingPayoutMax?: number
  ) =>
    normalizeStakeValue(stake, {
      betMin: options.betMin,
      betMax: options.betMax,
      payoutMax: payoutLimit,
      remainingPayoutMax,
      odds: line.odds,
      drawCount: line.drawCount ?? 1,
      numBets: line.numBets ?? 1,
      allowDecimals,
    })

  // Pass 1: bet_min / bet_max only (no payout cap yet)
  let stakes = lines.map((line) => normalizeLineStake(line.stake, line, 0))

  const calcTotalPotential = (currentStakes: number[]) =>
    lines.reduce(
      (sum, line, index) =>
        sum +
        calcPotentialWin(
          currentStakes[index],
          line.odds,
          line.drawCount ?? 1,
          line.numBets ?? 1
        ),
      0
    )

  let totalPotential = calcTotalPotential(stakes)
  if (totalPotential <= cap) {
    return stakes
  }

  // Pass 2: scale all stakes down so combined potential fits payout_max
  const scaleFactor = cap / totalPotential
  stakes = lines.map((line, index) =>
    normalizeLineStake(
      floorStakeAmount(stakes[index] * scaleFactor, allowDecimals),
      line,
      0
    )
  )

  totalPotential = calcTotalPotential(stakes)
  if (totalPotential <= cap) {
    return stakes
  }

  // Pass 3: trim from the end for rounding / bet_min edge cases
  for (let index = lines.length - 1; index >= 0 && calcTotalPotential(stakes) > cap; index--) {
    const line = lines[index]
    const otherPotential = lines.reduce(
      (sum, otherLine, otherIndex) =>
        otherIndex === index
          ? sum
          : sum +
            calcPotentialWin(
              stakes[otherIndex],
              otherLine.odds,
              otherLine.drawCount ?? 1,
              otherLine.numBets ?? 1
            ),
      0
    )
    const remainingPayoutMax = Math.max(0, cap - otherPotential)
    stakes[index] = normalizeLineStake(stakes[index], line, cap, remainingPayoutMax)
  }

  return stakes
}

/** Scale line potentials so displayed winnings sum to payout_max when over cap. */
export function allocateProportionalPotentialWins(
  lines: BetLinePotentialInput[],
  payoutMax: number
): number[] {
  const raw = lines.map((line) =>
    calcPotentialWin(line.stake, line.odds, line.drawCount ?? 1, line.numBets ?? 1)
  )
  const total = raw.reduce((sum, value) => sum + value, 0)
  const cap = toNumber(payoutMax)
  if (cap <= 0 || total <= cap || lines.length === 0) {
    return raw
  }

  const factor = cap / total
  const allocated = raw.map((value) => Math.floor(value * factor * 100) / 100)
  const allocatedSum = allocated.reduce((sum, value) => sum + value, 0)
  const remainder = Math.round((cap - allocatedSum) * 100) / 100
  if (remainder !== 0) {
    allocated[allocated.length - 1] =
      Math.round((allocated[allocated.length - 1] + remainder) * 100) / 100
  }
  return allocated
}

/** True potential win for display (stake × odds); only clips when still above cap. */
export function displayPotentialWin(
  stake: number,
  odds: number,
  payoutMax: number,
  drawCount = 1,
  numBets = 1
): number {
  const potentialWin = calcPotentialWin(stake, odds, drawCount, numBets)
  const payoutCap = toNumber(payoutMax)
  if (payoutCap <= 0) return potentialWin
  return potentialWin > payoutCap ? payoutCap : potentialWin
}
